import React, { useRef, useEffect } from 'react';
import './MultiSelectDropdown.scss';
import { Button } from 'components/form/Button/Button';
import { validateIncome } from 'helpers/common';
import { ReactComponent as CloseIcon } from 'style/_assets/icons/close.svg';
import { ReactComponent as ChevronDown } from 'style/_assets/icons/chevron-down.svg';
import { commafy, decommafy } from 'helpers/number';
import { formatIncome, unformatIncome } from 'helpers/money.helper';

function useHandleFocus(ref, setOpen) {
  function handleClickOutside(event) {
    if (ref.current && !ref.current.contains(event.target) && setOpen) {
      setOpen(false);
    }
  }

  useEffect(() => {
    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });
}

const mapValueToText = num => {
  switch (num) {
    case 1:
      return 'All Rating';
    case 2:
      return 'Four stars and up';
    case 3:
      return 'Three stars and up';
    case 4:
      return 'Two stars and up';
    case 5:
      return 'One star and up';
    default:
      return '';
  }
};

const mapValueToLabel = (options, value) => {
  for (let i = 0; i < options.length; i++) {
    if (options[i].value === value) {
      return options[i].label;
    }
  }
};

const MultiSelectDropdownButtonView = ({
  datatestid,
  initialText,
  type,
  values,
  options,
  onChange,
  defaultValue,
  radioUseOptions,
  keyFilter,
}) => {
  const handleCloseCheckbox = e => {
    e.stopPropagation();
    onChange([]);
  };
  const handleCloseSlider = () => {
    onChange(defaultValue ? defaultValue : '');
  };

  if (type === 'checkbox') {
    if (values.length === 0) {
      return (
        <div
          data-testid={datatestid ? `${datatestid}-button-view` : null}
          className={'multiselect-button-view'}
        >
          {initialText}
          <span className="multiselect-button-view-down-icon">
            <ChevronDown />
          </span>
        </div>
      );
    } else if (values.length === 1) {
      return (
        <div
          data-testid={datatestid ? `${datatestid}-button-view` : null}
          className={'multiselect-button-view'}
        >
          <span
            className="multiselect-button-view-value"
            data-testid={datatestid ? `${datatestid}-button-value` : null}
          >
            {mapValueToLabel(options, values[0])}
          </span>
          <span
            className="multiselect-button-view-close"
            onClick={e => handleCloseCheckbox(e)}
          >
            <CloseIcon />
          </span>
        </div>
      );
    } else if (values.length > 1) {
      return (
        <div
          data-testid={datatestid ? `${datatestid}-button-view` : null}
          className={'multiselect-button-view'}
        >
          <span
            className="multiselect-button-view-value"
            data-testid={datatestid ? `${datatestid}-button-value` : null}
          >
            {initialText}
          </span>
          <span className="mulitselect-middle-dot">
            {' '}
            &#xb7; {values.length}{' '}
          </span>
          <span
            className="multiselect-button-view-close"
            onClick={e => handleCloseCheckbox(e)}
          >
            <CloseIcon />
          </span>
        </div>
      );
    }
  } else if (type === 'slider') {
    return (
      <div
        data-testid={datatestid ? `${datatestid}-button-view` : null}
        className={'multiselect-button-view'}
      >
        <span
          className="multiselect-button-view-value"
          data-testid={datatestid ? `${datatestid}-button-value` : null}
        >
          {'Loan $' + commafy(values)}
        </span>
        <span
          className="multiselect-button-view-close"
          onClick={handleCloseSlider}
        >
          <CloseIcon />
        </span>
      </div>
    );
  } else if (type === 'input') {
    return (
      <div
        data-testid={datatestid ? `${datatestid}-button-view` : null}
        className={'multiselect-button-view'}
      >
        <span
          className="multiselect-button-view-value"
          data-testid={datatestid ? `${datatestid}-button-value` : null}
        >
          {'Income ' + commafy(values)}
        </span>
        <span
          className="multiselect-button-view-close"
          onClick={() => {
            onChange({
              value: unformatIncome(defaultValue),
              error: !validateIncome(defaultValue, true)
                ? ''
                : validateIncome(defaultValue, true),
              label: formatIncome(unformatIncome(defaultValue)),
              validated:
                typeof validateIncome(defaultValue, true) === 'boolean'
                  ? true
                  : false,
            });
          }}
        >
          <CloseIcon />
        </span>
      </div>
    );
  } else if (type === 'radio') {
    let text = initialText;
    let icon = <ChevronDown />;
    let iconClass = 'multiselect-button-view-down-icon';
    if (values) {
      text = radioUseOptions
        ? mapValueToLabel(options, values)
        : mapValueToText(values);
      icon = <CloseIcon />;
      iconClass = 'multiselect-button-view-close';
    }
    return (
      <div
        data-testid={datatestid ? `${datatestid}-button-view` : null}
        className={'multiselect-button-view'}
      >
        <span
          className="multiselect-button-view-value"
          data-testid={datatestid ? `${datatestid}-button-value` : null}
        >
          {text}
        </span>
        <span className={iconClass} onClick={() => onChange('')}>
          {icon}
        </span>
      </div>
    );
  } else if (!type) {
    // for no childrens (no dropdown will be render, just a tag!)
    return (
      <div
        data-testid={datatestid ? `${datatestid}-button-view` : null}
        className={'multiselect-button-view'}
      >
        <span
          className="multiselect-button-view-value"
          data-testid={datatestid ? `${datatestid}-button-value` : null}
        >
          {mapValueToLabel(options, values[0])}
        </span>
        <span
          className="multiselect-button-view-close"
          onClick={() => onChange(keyFilter)}
        >
          <CloseIcon />
        </span>
      </div>
    );
  }
};

function MultiSelectDropdown(props) {
  const {
    datatestid,
    className,
    type,
    defaultValue,
    initialText,
    values,
    options,
    onChange,
    isOpen,
    setOpen,
    keyFilter,
    children,
    radioUseOptions,
    isRoundedButton,
  } = props;
  const wrapperRef = useRef(null);
  useHandleFocus(wrapperRef, setOpen);

  const handleClick = () => {
    if (setOpen) {
      setOpen(prev => !prev);
    }
  };

  const getClassname = () => {
    if (type !== 'input') {
      if (isOpen) {
        if (values?.length > 0) {
          return ' multiselectdropdown-container isOpen selected ';
        } else {
          return ' multiselectdropdown-container isOpen ';
        }
      } else {
        if (values?.length > 0 && children) {
          return ' multiselectdropdown-container selected ';
        } else {
          return ' multiselectdropdown-container ';
        }
      }
    } else {
      if (isOpen) {
        if (parseInt(decommafy(values), 10) !== defaultValue) {
          return ' multiselectdropdown-container isOpen selected ';
        } else {
          return ' multiselectdropdown-container isOpen ';
        }
      } else {
        if (parseInt(decommafy(values), 10) !== defaultValue) {
          return ' multiselectdropdown-container isOpen selected ';
        } else {
          return ' multiselectdropdown-container ';
        }
      }
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={className + getClassname()}
      data-testid={datatestid ? `${datatestid}-container` : null}
    >
      <Button onClick={handleClick} rounded={isRoundedButton}>
        <MultiSelectDropdownButtonView
          options={options}
          datatestid={datatestid}
          initialText={initialText}
          type={type}
          defaultValue={defaultValue}
          values={values}
          onChange={onChange}
          setOpen={setOpen}
          isOpen={isOpen}
          radioUseOptions={radioUseOptions}
          keyFilter={keyFilter}
        />
      </Button>
      {isOpen && children && (
        <div className="multiselectdropdown-body-container">{children} </div>
      )}
    </div>
  );
}

export { MultiSelectDropdown };
